import React, { useEffect, useState } from "react";
import { API_BASE, apiFetch } from "../../api/http";

interface ClientTemplate {
  documentType: "protocol" | "protective" | "clause";
  fileName: string;
  url?: string;
}

interface ClientTemplateListModalProps {
  isOpen: boolean;
  onClose: () => void;
  clientName?: string;
  projectNumber?: string;
  onDeleted?: () => void;
}

// Use shared API base (deployment-safe)

const ClientTemplateListModal: React.FC<ClientTemplateListModalProps> = ({ isOpen, onClose, clientName, projectNumber, onDeleted }) => {
  const [templates, setTemplates] = useState<ClientTemplate[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const name = clientName || localStorage.getItem('clientName') || '';
      const number = projectNumber || localStorage.getItem('projectNumber') || '';
      const res = await apiFetch(
        `${API_BASE}/api/clientTemplates?clientName=${encodeURIComponent(name)}&projectNumber=${encodeURIComponent(number)}`
      );
      if (!res.ok) {
        const errJson = await res.json().catch(() => ({}));
        throw new Error(errJson.error || res.statusText || 'Failed to fetch templates');
      }
      const data: ClientTemplate[] = await res.json();
      setTemplates(data);
    } catch (err) {
      console.error("Error fetching templates", err);
      alert('Error loading templates: ' + (err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (documentType: string) => {
    const name = clientName || localStorage.getItem('clientName') || '';
    const number = projectNumber || localStorage.getItem('projectNumber') || '';
    if (!name || !number) {
      alert('Cannot delete: client name and project number required.');
      return;
    }
    setLoading(true);
    try {
      const res = await apiFetch(
        `${API_BASE}/api/clientTemplates/${encodeURIComponent(documentType)}?clientName=${encodeURIComponent(name)}&projectNumber=${encodeURIComponent(number)}`,
        { method: "DELETE" }
      );
      if (!res.ok) {
        const errJson = await res.json().catch(() => ({}));
        throw new Error(errJson.error || res.statusText || 'Delete failed');
      }
      onDeleted?.();
      // Always await fetchTemplates and only then setLoading(false)
      await fetchTemplates();
    } catch (err) {
      console.error("Error deleting template", err);
      alert('Error deleting template: ' + (err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) fetchTemplates();
  }, [isOpen, clientName, projectNumber]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-lg w-full max-w-md p-6 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          aria-label="Close"
          className="absolute top-3 right-3 text-gray-500 hover:text-gray-700 text-xl"
          onClick={onClose}
        >
          &times;
        </button>

        <h2 className="text-xl font-semibold mb-4">Uploaded Templates</h2>

        {loading ? (
          <p className="text-gray-500">Loading...</p>
        ) : templates.length === 0 ? (
          <p className="text-gray-500">No templates uploaded yet.</p>
        ) : (
          <div className="max-h-64 overflow-y-auto pr-2">
            <table className="min-w-full text-sm border border-gray-200 rounded">
              <thead>
                <tr className="bg-gray-100">
                  <th className="px-3 py-2 text-left font-semibold">Document Type</th>
                  <th className="px-3 py-2 text-left font-semibold">Document Name</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {templates.map((t) => (
                  <tr key={t.documentType} className="border-t border-gray-200">
                    <td className="px-3 py-2 font-medium capitalize">{t.documentType}</td>
                    <td className="px-3 py-2 truncate max-w-xs">
                      <a
                        href={t.url || "#"}
                        target="_blank"
                        rel="noreferrer"
                        className="text-brand-500 hover:underline"
                      >
                        {t.fileName}
                      </a>
                    </td>
                    <td className="px-3 py-2">
                      <button
                        onClick={() => handleDelete(t.documentType)}
                        className="text-red-600 hover:text-red-800 text-sm"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientTemplateListModal; 